import React, { useEffect, useMemo, useState } from "react";
import CalculatieActionBar from "./CalculatieActionBar";
import CalculatieHeader from "./CalculatieHeader";
import CalculatieLaborSection from "./CalculatieLaborSection";
import CalculatieMaterialSection from "./CalculatieMaterialSection";
import CalculatieOtherCostsSection from "./CalculatieOtherCostsSection";
import CalculatieTotalsPanel from "./CalculatieTotalsPanel";
import CalculatieValidationPanel from "./CalculatieValidationPanel";
import PricingSourceBadge from "./PricingSourceBadge";
import {
  buildValidationItems,
  computeTotals,
  getClientName,
  getClientNumber,
  getProjectName,
  getProjectNumber,
} from "./utils";
import { formatPricingDate, getPricingSourceSummary } from "../../utils/pricingUi";

export default function CalculatieWorkspace({
  form,
  editingId,
  clients,
  opnames,
  projects,
  saving,
  applying,
  copying,
  duplicating,
  tuPunchoutLoading,
  selectedClient,
  selectedOpname,
  selectedProject,
  selectedCalculatie,
  hourTypes,
  error,
  success,
  showFinancials,
  duplicatePreview,
  duplicatePreviewLoading,
  refreshPreview,
  refreshPreviewLoading,
  onSubmit,
  onReset,
  onFieldChange,
  onLineChange,
  onHourTypeChange,
  onAddLine,
  onRemoveLine,
  onApplyToProject,
  onCopy,
  onDuplicateSameClient,
  onDuplicate,
  onPreviewDuplicate,
  onPreviewRefreshPricing,
  onApplyRefreshedPricing,
  onOpenPdf,
  onOpenCatalog,
}) {
  const [openSections, setOpenSections] = useState({
    labor: true,
    material: true,
    other: false,
    assumptions: false,
    notes: false,
  });
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateClientId, setDuplicateClientId] = useState("");
  const [duplicateContactPersonId, setDuplicateContactPersonId] = useState("");
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [duplicateError, setDuplicateError] = useState("");
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [refreshPricingDate, setRefreshPricingDate] = useState("");
  const [pricingDetail, setPricingDetail] = useState(null);

  const totals = useMemo(() => computeTotals(form), [form]);
  const validationItems = useMemo(
    () =>
      buildValidationItems({
        form,
        totals,
        editingId,
        selectedProject,
        selectedCalculatie,
        showFinancials,
      }),
    [editingId, form, selectedCalculatie, selectedProject, showFinancials, totals]
  );

  const canApply = Boolean(editingId && form.projectId);
  const availableContacts = Array.isArray(selectedClient?.contactPersons)
    ? selectedClient.contactPersons.filter((item) => item?.active !== false)
    : [];
  const filteredOpnames = useMemo(() => {
    if (!form.clientId) return opnames;
    return opnames.filter(
      (item) => String(item?.clientId?._id || item?.clientId || "") === String(form.clientId)
    );
  }, [form.clientId, opnames]);
  const filteredProjects = useMemo(() => {
    if (!form.clientId) return projects;
    return projects.filter(
      (item) => String(item?.clientId?._id || item?.clientId || "") === String(form.clientId)
    );
  }, [form.clientId, projects]);
  const duplicateClient = useMemo(
    () => clients.find((item) => String(item._id) === String(duplicateClientId)) || null,
    [clients, duplicateClientId]
  );
  const duplicateContacts = Array.isArray(duplicateClient?.contactPersons)
    ? duplicateClient.contactPersons.filter((item) => item?.active !== false)
    : [];

  useEffect(() => {
    setDuplicateOpen(false);
    setDuplicateClientId("");
    setDuplicateContactPersonId("");
    setDuplicateTitle(String(selectedCalculatie?.title || form.title || "").trim());
    setDuplicateError("");
    setRefreshOpen(false);
    setRefreshPricingDate(String(selectedCalculatie?.pricingDate || form.pricingDate || "").slice(0, 10));
    setPricingDetail(null);
  }, [editingId, form.pricingDate, form.title, selectedCalculatie?.pricingDate, selectedCalculatie?._id, selectedCalculatie?.title]);

  async function handleDuplicatePreview() {
    if (!editingId) return;
    if (!duplicateClientId) {
      setDuplicateError("Kies eerst de doelklant voor de kopie.");
      return;
    }
    if (String(duplicateClientId) === String(form.clientId || "")) {
      setDuplicateError("Gebruik dupliceren voor dezelfde klant; deze actie is alleen voor een andere klant.");
      return;
    }

    try {
      setDuplicateError("");
      await onPreviewDuplicate({
        clientId: duplicateClientId,
        contactPersonId: duplicateContactPersonId,
        title: duplicateTitle,
      });
    } catch {
      // Pagina-level foutmelding blijft leidend; lokaal houden we alleen de inline validatie.
    }
  }

  async function handleDuplicateConfirm() {
    if (!editingId) return;
    try {
      setDuplicateError("");
      await onDuplicate({
        clientId: duplicateClientId,
        contactPersonId: duplicateContactPersonId,
        title: duplicateTitle,
      });
      setDuplicateOpen(false);
    } catch {
      // Pagina-level foutmelding blijft leidend; lokaal houden we alleen de inline validatie.
    }
  }

  async function handleRefreshPreview() {
    if (!editingId) return;
    await onPreviewRefreshPricing({
      pricingDate: refreshPricingDate,
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <CalculatieHeader
        form={form}
        editingId={editingId}
        selectedClient={selectedClient}
        selectedOpname={selectedOpname}
        selectedProject={selectedProject}
        selectedCalculatie={selectedCalculatie}
      >
        <div className="space-y-2">
          <CalculatieActionBar
            editingId={editingId}
            saving={saving}
            copying={copying}
            duplicating={duplicating}
            onCopy={onCopy}
            onDuplicateSameClient={onDuplicateSameClient}
            onToggleDuplicate={() => {
              setDuplicateOpen((prev) => !prev);
              setDuplicateError("");
              setDuplicateTitle(String(selectedCalculatie?.title || form.title || "").trim());
            }}
            onToggleRefreshPricing={() => setRefreshOpen((prev) => !prev)}
            onOpenPdf={onOpenPdf}
            onOpenCatalog={onOpenCatalog}
          />
          {editingId ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onReset}
                className="text-xs font-medium text-slate-500 transition hover:text-slate-700"
              >
                Bewerken annuleren
              </button>
            </div>
          ) : null}
        </div>
      </CalculatieHeader>

      {editingId && duplicateOpen ? (
        <section className="border border-amber-200 bg-amber-50 px-4 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <Field label="Doelklant">
              <select
                value={duplicateClientId}
                onChange={(e) => {
                  setDuplicateClientId(e.target.value);
                  setDuplicateContactPersonId("");
                }}
                className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900"
              >
                <option value="">Kies andere klant</option>
                {clients
                  .filter((client) => String(client._id) !== String(form.clientId || ""))
                  .map((client) => (
                    <option key={client._id} value={client._id}>
                      {getClientNumber(client)} - {getClientName(client)}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label="Contactpersoon">
              <select
                value={duplicateContactPersonId}
                onChange={(e) => setDuplicateContactPersonId(e.target.value)}
                disabled={!duplicateClientId}
                className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900"
              >
                <option value="">{duplicateClientId ? "Geen contactpersoon" : "Kies eerst klant"}</option>
                {duplicateContacts.map((contact) => (
                  <option key={contact._id} value={contact._id}>
                    {contact.name || "-"}{contact.role ? ` - ${contact.role}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Titel kopie">
              <input
                value={duplicateTitle}
                onChange={(e) => setDuplicateTitle(e.target.value)}
                className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900"
                placeholder="Titel van de kopie"
              />
            </Field>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDuplicatePreview}
                disabled={duplicatePreviewLoading || duplicating}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:opacity-60"
              >
                {duplicatePreviewLoading ? "Preview laden..." : "Prijsverschil tonen"}
              </button>
              {duplicatePreview ? (
                <button
                  type="button"
                  onClick={handleDuplicateConfirm}
                  disabled={duplicating}
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-60"
                >
                  {duplicating ? "Kopieert..." : "Kopie definitief maken"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setDuplicateOpen(false);
                  setDuplicateError("");
                }}
                className="rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-amber-100"
              >
                Annuleren
              </button>
            </div>
          </div>

          <p className="mt-3 text-xs text-amber-900">
            Inhoud blijft gelijk, maar klantcontext, contact en versiereeks starten opnieuw. Project- en opnamekoppeling worden bewust niet meegenomen.
          </p>
          {duplicatePreview ? (
            <div className="mt-4 rounded-md border border-amber-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-900">Preview andere klant</p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <PreviewMetric label="Huidige verkoop" value={`€ ${totalsToMoney(duplicatePreview?.totals?.source?.totalSell)}`} />
                <PreviewMetric label="Nieuwe verkoop" value={`€ ${totalsToMoney(duplicatePreview?.totals?.target?.totalSell)}`} />
                <PreviewMetric label="Verschil" value={`€ ${totalsToMoney((duplicatePreview?.totals?.target?.totalSell || 0) - (duplicatePreview?.totals?.source?.totalSell || 0))}`} />
              </div>
              <div className="mt-4 space-y-2 text-xs text-slate-700">
                {renderPreviewLines("Arbeid", duplicatePreview?.laborLines)}
                {renderPreviewLines("Materiaal", duplicatePreview?.materialLines)}
                {renderPreviewLines("Overig", duplicatePreview?.otherCostLines)}
              </div>
            </div>
          ) : null}
          {duplicateError ? <p className="mt-2 text-sm text-rose-700">{duplicateError}</p> : null}
        </section>
      ) : null}

      {editingId && refreshOpen ? (
        <section className="border border-violet-200 bg-violet-50 px-4 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <Field label="Nieuwe prijspeildatum">
              <input
                type="date"
                value={refreshPricingDate}
                onChange={(event) => setRefreshPricingDate(event.target.value)}
                className="w-full rounded-md border border-violet-300 bg-white px-3 py-2 text-sm text-slate-900"
              />
            </Field>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleRefreshPreview}
                disabled={refreshPreviewLoading}
                className="rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-60"
              >
                {refreshPreviewLoading ? "Preview laden..." : "Prijspreview tonen"}
              </button>
              {refreshPreview ? (
                <button
                  type="button"
                  onClick={() => onApplyRefreshedPricing({ pricingDate: refreshPricingDate })}
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100"
                >
                  Prijzen definitief actualiseren
                </button>
              ) : null}
            </div>
          </div>

          {refreshPreview ? (
            <div className="mt-4 rounded-md border border-violet-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-900">Refresh preview</p>
              <p className="mt-1 text-xs text-slate-500">
                Huidig: {formatPricingDate(refreshPreview.currentPricingDate)} · Nieuw: {formatPricingDate(refreshPreview.nextPricingDate)}
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <PreviewMetric label="Huidige verkoop" value={`€ ${totalsToMoney(refreshPreview?.totals?.source?.totalSell)}`} />
                <PreviewMetric label="Nieuwe verkoop" value={`€ ${totalsToMoney(refreshPreview?.totals?.target?.totalSell)}`} />
                <PreviewMetric label="Verkoopverschil" value={`€ ${totalsToMoney(refreshPreview?.totals?.delta?.totalSell)}`} />
              </div>
              {showFinancials ? (
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <PreviewMetric label="Kostverschil" value={`€ ${totalsToMoney(refreshPreview?.totals?.delta?.totalCost)}`} />
                  <PreviewMetric label="Margeverschil" value={`€ ${totalsToMoney(refreshPreview?.totals?.delta?.margin)}`} />
                  <PreviewMetric label="Marge % verschil" value={`${Number(refreshPreview?.totals?.delta?.marginPct || 0).toFixed(1)}%`} />
                </div>
              ) : null}
              {Array.isArray(refreshPreview?.warnings) && refreshPreview.warnings.length > 0 ? (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {refreshPreview.warnings.map((warning, index) => (
                    <p key={`${warning}-${index}`}>{warning}</p>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2.4fr)_minmax(320px,1fr)]">
        <div className="space-y-4">
          <section className="border border-slate-200 bg-white px-4 py-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1.1fr_1.1fr_1fr_1fr_0.9fr_0.9fr]">
              <Field label="Relatie / Context">
                <select
                  value={form.clientId}
                  onChange={(e) => onFieldChange("clientId", e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  required
                >
                  <option value="">Kies klant / opdrachtgever</option>
                  {clients.map((client) => (
                    <option key={client._id} value={client._id}>
                      {getClientNumber(client)} - {getClientName(client)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contactpersoon">
                <select
                  value={form.contactPersonId}
                  onChange={(e) => onFieldChange("contactPersonId", e.target.value)}
                  disabled={!form.clientId}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="">{form.clientId ? "Geen contactpersoon" : "Kies eerst klant"}</option>
                  {availableContacts.map((contact) => (
                    <option key={contact._id} value={contact._id}>
                      {contact.name || "-"}{contact.role ? ` - ${contact.role}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Bron">
                <select
                  value={form.sourceType}
                  onChange={(e) => onFieldChange("sourceType", e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="manual">Handmatig</option>
                  <option value="website">Website</option>
                  <option value="customer_portal">Klantportaal</option>
                  <option value="opname">Opname</option>
                  <option value="project">Project</option>
                </select>
              </Field>
              <Field label="Bronreferentie">
                <input
                  value={form.sourceReference}
                  onChange={(e) => onFieldChange("sourceReference", e.target.value)}
                  placeholder="Aanvraagnummer, lead of referentie"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Opname optioneel">
                <select
                  value={form.opnameId}
                  onChange={(e) => onFieldChange("opnameId", e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="">Geen opname gekoppeld</option>
                  {filteredOpnames.map((opname) => (
                    <option key={opname._id} value={opname._id}>
                      {opname.titel || opname.title || "Opname"}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Project optioneel">
                <select
                  value={form.projectId}
                  onChange={(e) => onFieldChange("projectId", e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="">Geen project gekoppeld</option>
                  {filteredProjects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {getProjectNumber(project)} - {getProjectName(project)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Prijspeildatum">
                <input
                  type="date"
                  value={form.pricingDate}
                  onChange={(e) => onFieldChange("pricingDate", e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                />
              </Field>
              <Field label="Titel">
                <input
                  value={form.title}
                  onChange={(e) => onFieldChange("title", e.target.value)}
                  placeholder="Calculatienaam"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  required
                />
              </Field>
              <Field label="Versie (systeem)">
                <input
                  type="number"
                  value={form.version}
                  readOnly
                  className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => onFieldChange("status", e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  <option value="concept">Concept</option>
                  <option value="definitief">Definitief</option>
                  <option value="goedgekeurd">Goedgekeurd</option>
                </select>
              </Field>
              <Field label="Werkmodus">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  Context-first calculatie
                </div>
              </Field>
            </div>
          </section>

          <CalculatieLaborSection
            lines={form.laborLines}
            hourTypes={hourTypes}
            open={openSections.labor}
            onToggle={() => setOpenSections((prev) => ({ ...prev, labor: !prev.labor }))}
            onChange={(index, key, value) => onLineChange("laborLines", index, key, value)}
            onHourTypeChange={onHourTypeChange}
            onAdd={() => onAddLine("laborLines")}
            onRemove={(index) => onRemoveLine("laborLines", index)}
            showFinancials={showFinancials}
            onOpenPricingDetail={showFinancials ? setPricingDetail : null}
          />

          <CalculatieMaterialSection
            lines={form.materialLines}
            open={openSections.material}
            onToggle={() => setOpenSections((prev) => ({ ...prev, material: !prev.material }))}
            onChange={(index, key, value) => onLineChange("materialLines", index, key, value)}
            onAdd={() => onAddLine("materialLines")}
            onRemove={(index) => onRemoveLine("materialLines", index)}
            showFinancials={showFinancials}
            onOpenPricingDetail={showFinancials ? setPricingDetail : null}
          />

          <CalculatieOtherCostsSection
            open={openSections.other}
            onToggle={() => setOpenSections((prev) => ({ ...prev, other: !prev.other }))}
          />

          {showFinancials && pricingDetail ? (
            <section className="border border-slate-200 bg-white px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Prijsbron detail</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {pricingDetail.lineType}regel {Number(pricingDetail.index) + 1}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPricingDetail(null)}
                  className="text-xs font-medium text-slate-500 transition hover:text-slate-700"
                >
                  Sluiten
                </button>
              </div>
              <PricingSnapshotDetail line={pricingDetail.line} showFinancials={showFinancials} />
            </section>
          ) : null}

          <CompactDisclosure
            title="Aannames"
            description="Compact voorbereid voor latere calculatie-uitbreiding zonder extra backendpayload."
            open={openSections.assumptions}
            onToggle={() => setOpenSections((prev) => ({ ...prev, assumptions: !prev.assumptions }))}
          >
            <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
              Gebruik deze sectie later voor uitgangspunten, prijspeildatum, scope-afbakening en commerciële aannames. In deze fase blijft dit voorbereid en niet-persistent.
            </div>
          </CompactDisclosure>

          <CompactDisclosure
            title="Interne notities"
            description="Interne toelichting voor overdracht, controle en PDF-uitdraai."
            open={openSections.notes}
            onToggle={() => setOpenSections((prev) => ({ ...prev, notes: !prev.notes }))}
          >
            <textarea
              value={form.notes}
              onChange={(e) => onFieldChange("notes", e.target.value)}
              placeholder="Interne notities, aannames of aandachtspunten"
              className="min-h-[120px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            />
          </CompactDisclosure>

          {error ? <p className="text-sm text-rose-700">{error}</p> : null}
          {success ? <p className="text-sm text-emerald-700">{success}</p> : null}
        </div>

        <div className="space-y-4">
          <CalculatieTotalsPanel
            totals={totals}
            selectedCalculatie={selectedCalculatie}
            applying={applying}
            onApplyToProject={onApplyToProject}
            canApply={canApply}
            tuPunchoutLoading={tuPunchoutLoading}
            showFinancials={showFinancials}
          />
          <CalculatieValidationPanel items={validationItems} />
        </div>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-1 text-sm">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function CompactDisclosure({ title, description, open, onToggle, children }) {
  return (
    <section className="border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 text-left"
      >
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-600">{description}</p>
        </div>
        <span className="text-xs font-medium text-slate-500">{open ? "Inklappen" : "Openen"}</span>
      </button>
      {open ? <div className="px-4 py-4">{children}</div> : null}
    </section>
  );
}

function PreviewMetric({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function totalsToMoney(value) {
  return Number(value || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function renderPreviewLines(label, rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <p key={label}>{label}: geen regels</p>;
  }
  const changed = rows.filter((row) => Number(row.deltaSell || 0) !== 0 || Number(row.deltaCost || 0) !== 0).length;
  return (
    <p key={label}>
      {label}: {changed} gewijzigde regel(s) van {rows.length}
    </p>
  );
}

function PricingSnapshotDetail({ line, showFinancials }) {
  const snapshot = line?.pricingSnapshot || null;
  const summary = getPricingSourceSummary(snapshot);

  if (!snapshot) {
    return (
      <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        Geen pricing snapshot beschikbaar voor deze regel.
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <PricingSourceBadge snapshot={snapshot} />
        <span className="text-sm text-slate-600">Snapshot {formatPricingDate(snapshot.pricingDate)}</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <PreviewMetric label="Verkoop" value={`€ ${totalsToMoney(snapshot.sellValue)}`} />
        {showFinancials ? <PreviewMetric label="Kost" value={`€ ${totalsToMoney(snapshot.costValue)}`} /> : null}
        {showFinancials ? <PreviewMetric label="Marge" value={`€ ${totalsToMoney(Number(snapshot.sellValue || 0) - Number(snapshot.costValue || 0))}`} /> : null}
        <PreviewMetric label="Bron" value={summary.label} />
      </div>
      <div className="grid gap-2 text-xs text-slate-600">
        <p>Agreement-id: {summary.agreementId || "-"}</p>
        <p>Agreement-versie: {summary.agreementVersion ? `v${summary.agreementVersion}` : "-"}</p>
        <p>Rule-id: {summary.ruleId || "-"}</p>
        <p>Scope: {summary.scopeType || "-"}</p>
      </div>
      {Array.isArray(snapshot.warnings) && snapshot.warnings.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {snapshot.warnings.map((warning, index) => (
            <p key={`${warning}-${index}`}>{warning}</p>
          ))}
        </div>
      ) : null}
      <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 text-xs text-slate-700">
        {JSON.stringify(
          {
            sellSource: snapshot.sellSource,
            costSource: snapshot.costSource,
            formulaInputs: snapshot.formulaInputs,
          },
          null,
          2
        )}
      </pre>
    </div>
  );
}
